import { Injectable, inject } from '@angular/core';
//import { AppInsightsService } from '@markpieszak/ng-application-insights';
import { AppInsightsService } from '../logging/app-insights-service';
import * as signalR from '@microsoft/signalr';
import { HttpResponse, HubConnectionState } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { IdentityService } from '../auth/identity.service';
import { EnvironmentService } from '../environment/environment.service';
import { returnRequest, sendRequest } from '../redux/http-status/http-status.actions';
import { SnackbarService } from '../snackbar/snackbar.service';
import { ApiUrls } from '../utility/apiurls-shared';
import { NotificationConstants } from '../utility/notification-constants';
import { NotificationLogger } from './notification-logger';

type NotifierFunction = (message: string) => void;

// Used for adding the security header to signalR requests
class NotificationHttpClient extends signalR.DefaultHttpClient {
  constructor(private identityService: IdentityService) {
    super(console);
  }

  public send(request: signalR.HttpRequest): Promise<HttpResponse> {
    request.headers = {
      ...request.headers,
      'bis-api-security': this.identityService.apiToken?.apiSecurityHash,
    };
    return super.send(request);
  }
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private identityService = inject(IdentityService);
  private snackbarService = inject(SnackbarService);
  private store = inject(Store);
  private notificationLogger = inject(NotificationLogger);
  private appInsightsService = inject(AppInsightsService);

  private notificationQueueConnection: signalR.HubConnection;
  private notifiers = new Map<string, NotifierFunction>();

  //Unhandled notifications are saved for situations where the name of the message is unknown before we send the request,
  //for example when an eform is submitted and we might get a new filing id. Because of this the caller is forced to register
  //the notification handler *after the request is sent*. 99.99% of the time this is fine, but sometimes network issues occur
  //causing the reply on the initial post request to return after the notification has already fired! This means that we are
  //registering the notifier after the notification was sent. To work around this: we replay unhandled notifications in situations
  //where this could occur including all filing notifications (Note that intial PAR submissions know the filing ID but do not wait for the notifier)
  //and optionally for all other notifiers based on the the "replayLastNotification" parameter
  private unhandledNotifications = new Map<string, string>();

  private startResult: Promise<void>;
  private statusDisplayLevel = signalR.LogLevel.Warning;

  
 

  constructor() {
    const identityService = this.identityService;
    const environmentService = inject(EnvironmentService);
    const apiUrls = inject(ApiUrls);

    this.notificationQueueConnection = new signalR.HubConnectionBuilder()
      .withUrl(apiUrls.notification.notificationHub(), {
        accessTokenFactory: () => this.identityService.apiToken?.accessToken,
        httpClient: new NotificationHttpClient(identityService),
      })
      .configureLogging(this.notificationLogger)
      .withAutomaticReconnect()
      .build();

    if (environmentService.getEnvironment().signalRShowNonErrorMessages) {
      this.statusDisplayLevel = signalR.LogLevel.Debug;
    }

    this.notificationQueueConnection.onclose(err => {
      if (err) {
        this.showStatusToUser(signalR.LogLevel.Error, 'Connection failed.');
      }
    });

    this.notificationQueueConnection.onreconnecting(err => {
      this.showStatusToUser(
        err ? signalR.LogLevel.Warning : signalR.LogLevel.Debug,
        'Reconnecting...',
      );
    });

    this.notificationQueueConnection.onreconnected(() => {
      this.showStatusToUser(signalR.LogLevel.Debug, 'Reconnected.');
    });

    this.notificationQueueConnection.on('Notify', (messageName, message) =>
      this.onNotifyReady(messageName, message),
    );
    identityService.accessToken$.subscribe(accessToken => {
      if (accessToken) {
        this.startResult = this.startNewConnection();
      } else {
        this.notificationQueueConnection.stop();
      }
    });
  }

  private showStatusToUser(level: signalR.LogLevel, message: string, isSuccess?: boolean) {
    if (this.statusDisplayLevel >= level) {
      return;
    }

    if (level >= signalR.LogLevel.Warning) {
      this.snackbarService.openSnackBarFailure(message);
    } else if (isSuccess) {
      this.snackbarService.openSnackBarSuccess(message);
    } else {
      this.snackbarService.openSnackBarInformation(message);
    }
  }

  private async startNewConnection(): Promise<void> {
    this.store.dispatch(sendRequest());
    try {
      this.showStatusToUser(signalR.LogLevel.Information, 'Connecting...');
      if (this.notificationQueueConnection.state !== HubConnectionState.Disconnected) {
        await this.notificationQueueConnection.stop();
      }
      await this.notificationQueueConnection.start();
      this.showStatusToUser(signalR.LogLevel.Information, 'Connected.', true);
    } catch (err) {
      this.startResult = null;
      this.store.dispatch(returnRequest());
      this.showStatusToUser(signalR.LogLevel.Error, 'Connection failed.');
      throw err;
    }
    this.startResult = null;
    this.store.dispatch(returnRequest());
  }

  ensureConnected(): Promise<void> {
    if (this.notificationQueueConnection.state === HubConnectionState.Connected) {
      this.startResult = null;
      return Promise.resolve();
    }
    if (!this.startResult) {
      this.startResult = this.startNewConnection();
    }
    return this.startResult;
  }

  setNotifier(
    name: string,
    func: NotifierFunction,
    replayLastNotification?: boolean, //See note above on unhandledNotifications for more info.
  ): Promise<void> {
    if (name === NotificationConstants.FilingConfirmation) {
      throw Error(
        'Use setFilingConfirmationNotifier to set the FilingConfirmation notifier function.',
      );
    }
    this.notifiers.set(name, func);
    const unhandledNotification = this.unhandledNotifications.get(name);
    if (unhandledNotification) {
      if (this.unhandledNotifications.delete(name)) {
        if (replayLastNotification) {
          func(unhandledNotification);
        }
      }
    }
    return this.ensureConnected();
  }

  removeNotifier(name: string, func: NotifierFunction) {
    if (name === NotificationConstants.FilingConfirmation) {
      throw Error(
        'Use setFilingConfirmationNotifier to set the FilingConfirmation notifier function.',
      );
    }
    if (this.notifiers.get(name) === func) {
      this.notifiers.delete(name);
    }
  }

  removeNotifiers(...funcs: NotifierFunction[]) {
    this.notifiers.forEach((func, name) => {
      if (funcs.includes(func)) {
        this.notifiers.delete(name);
      }
    });
  }

  setFilingConfirmationNotifier(filingSubmissionId: number, func: NotifierFunction): Promise<void> {
    //When we set the filing confirmation notifier, we always assume that we should replay unhandled notifications.
    //Initial par submissions technically always know the filing ID but do not wait on the notifier, so we do not provide option of not replaying.
    //See note above on unhandledNotifications for more info.
    const name = `${NotificationConstants.FilingConfirmation}-${filingSubmissionId}`;
    this.notifiers.set(name, func);
    const unhandledNotification = this.unhandledNotifications.get(name);
    if (unhandledNotification) {
      if (this.unhandledNotifications.delete(name)) {
        func(unhandledNotification);
      }
    }
    return this.ensureConnected();
  }

  removeFilingConfirmationNotifier(filingSubmissionId: number, func: NotifierFunction) {
    const name = `${NotificationConstants.FilingConfirmation}-${filingSubmissionId}`;
    if (this.notifiers.get(name) === func) {
      this.notifiers.delete(name);
    }
  }

  private onNotifyReady(messageName: string, message: string) {
    const notifier = this.notifiers.get(messageName);

    // this.appInsightsService.trackTrace('SignalR Notification Received', {
    //   MessageName: messageName,
    //   NotifierFound: notifier ? 'true' : 'false',
    // });

    this.appInsightsService.trackTrace('SignalR Notification Received');

    if (notifier) {
      notifier(message);
    } else {
      this.unhandledNotifications.set(messageName, message);
    }
  }
}
